import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import type { OktaAuth } from '@okta/okta-auth-js';
import { environment } from '../../environments/environment';
import { clearPortalSessionState } from './portal-session-storage';

/** Enforces 30 minutes of inactivity independently of the access-token lifetime. */
@Injectable({ providedIn: 'root' })
export class SessionTimeoutService {
  private static readonly IDLE_MS = 30 * 60_000;
  private static readonly WARNING_LEAD_MS = 2 * 60_000;
  private static readonly ACTIVITY_THROTTLE_MS = 1000;
  private static readonly LAST_ACTIVITY_KEY = 'hubCentral.lastActivity';
  private readonly oktaAuth = inject<OktaAuth>(OKTA_AUTH);
  private readonly authStateService = inject(OktaAuthStateService);
  private readonly destroyRef = inject(DestroyRef);
  readonly warningOpen = signal(false);
  readonly remainingSeconds = signal(0);
  readonly extending = signal(false);
  readonly remainingLabel = computed(() => {
    const seconds = this.remainingSeconds();
    return `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
  });
  private active = false;
  private signingOut = false;
  private hadAuthenticatedSession = false;
  private deadline = 0;
  private tokenDeadline = Number.POSITIVE_INFINITY;
  private lastEvent = 0;
  private interval?: ReturnType<typeof setInterval>;
  private readonly activity = () => this.recordActivity();
  private readonly visibility = () => this.checkDeadline();
  private readonly storage = (event: StorageEvent) => {
    if (event.key === SessionTimeoutService.LAST_ACTIVITY_KEY) this.checkDeadline();
  };

  constructor() {
    this.authStateService.authState$.pipe(takeUntilDestroyed()).subscribe(state => {
      if (state?.isAuthenticated) {
        this.hadAuthenticatedSession = true;
        this.updateTokenDeadline(state);
        if (!this.active && !this.signingOut) this.start();
      } else if (state != null) {
        if (this.hadAuthenticatedSession && !this.signingOut) void this.logout();
        else this.stop();
      }
    });
    this.destroyRef.onDestroy(() => this.stop());
  }

  /** A token may expire before the inactivity timer. Warn before whichever comes first. */
  private updateTokenDeadline(state: { accessToken?: { expiresAt?: number } } | null): void {
    const expiry = state?.accessToken?.expiresAt;
    this.tokenDeadline = typeof expiry === 'number' && Number.isFinite(expiry)
      ? expiry * 1000 : Number.POSITIVE_INFINITY;
  }

  private start(): void {
    this.active = true;
    const stored = this.readLastActivity();
    this.deadline = (stored || Date.now()) + SessionTimeoutService.IDLE_MS;
    if (!stored) this.persistActivity(Date.now());
    for (const event of ['pointerdown', 'keydown', 'scroll', 'touchstart']) {
      document.addEventListener(event, this.activity, { passive: true });
    }
    document.addEventListener('visibilitychange', this.visibility);
    window.addEventListener('focus', this.visibility);
    window.addEventListener('storage', this.storage);
    this.interval = setInterval(() => this.checkDeadline(), 1000);
    this.checkDeadline();
  }

  private stop(): void {
    this.active = false;
    clearInterval(this.interval);
    this.interval = undefined;
    for (const event of ['pointerdown', 'keydown', 'scroll', 'touchstart']) {
      document.removeEventListener(event, this.activity);
    }
    document.removeEventListener('visibilitychange', this.visibility);
    window.removeEventListener('focus', this.visibility);
    window.removeEventListener('storage', this.storage);
    this.warningOpen.set(false);
  }

  private readLastActivity(): number {
    try { return Number(localStorage.getItem(SessionTimeoutService.LAST_ACTIVITY_KEY)) || 0; }
    catch { return 0; }
  }

  private persistActivity(now: number): void {
    try { localStorage.setItem(SessionTimeoutService.LAST_ACTIVITY_KEY, String(now)); }
    catch { /* Storage can be disabled; this tab still tracks inactivity. */ }
  }

  private recordActivity(): void {
    if (!this.active || this.signingOut || this.warningOpen()) return;
    const now = Date.now();
    if (now - this.lastEvent < SessionTimeoutService.ACTIVITY_THROTTLE_MS) return;
    this.lastEvent = now;
    // Do not revive an expired session after the browser wakes from sleep.
    if (now >= this.deadline) { this.checkDeadline(); return; }
    this.deadline = now + SessionTimeoutService.IDLE_MS;
    this.persistActivity(now);
  }

  private checkDeadline(): void {
    if (!this.active || this.signingOut) return;
    const stored = this.readLastActivity();
    if (stored) this.deadline = Math.max(this.deadline, stored + SessionTimeoutService.IDLE_MS);
    const remaining = Math.max(0, Math.ceil((Math.min(this.deadline, this.tokenDeadline) - Date.now()) / 1000));
    this.remainingSeconds.set(remaining);
    if (remaining === 0) { void this.logout(); return; }
    this.warningOpen.set(remaining <= SessionTimeoutService.WARNING_LEAD_MS / 1000);
  }

  /** Explicit confirmation resets the idle clock; it does not silently renew tokens. */
  async staySignedIn(): Promise<void> {
    if (this.extending() || this.signingOut) return;
    this.extending.set(true);
    try {
      // Refresh credentials only on an explicit choice, not on ordinary activity.
      const tokens = await this.oktaAuth.token.renewTokens();
      const accessToken = tokens?.accessToken;
      if (!accessToken || typeof accessToken.expiresAt !== 'number' || accessToken.expiresAt <= Date.now() / 1000) {
        throw new Error('Renewed access token has no valid expiry');
      }
      this.oktaAuth.tokenManager.setTokens(tokens);
      const now = Date.now();
      this.deadline = now + SessionTimeoutService.IDLE_MS;
      this.tokenDeadline = accessToken.expiresAt * 1000;
      this.persistActivity(now);
      this.warningOpen.set(false);
    } catch (error) {
      console.error('Session extension failed.', error);
      await this.logout();
    } finally { this.extending.set(false); }
  }

  async logout(): Promise<void> {
    if (this.signingOut) return;
    this.signingOut = true;
    this.stop();
    clearPortalSessionState();
    // Okta signOut needs the ID token to construct the end-session request.
    // Clearing tokens first can prevent remote logout and allow the SSO session
    // to authenticate the user again on the next protected route.
    try {
      await Promise.race([
        this.oktaAuth.signOut({ postLogoutRedirectUri: environment.okta.postLogoutRedirectUri }),
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Logout timed out')), 5000)),
      ]);
    } catch (error) {
      console.error('Remote sign-out failed; clearing local session.', error);
      try { await this.oktaAuth.tokenManager.clear(); }
      catch (clearError) { console.error('Token clearing failed.', clearError); }
      // This is a public route; never navigate to a guarded dashboard on failure.
      window.location.replace('/signed-out');
    }
  }
}
